import first from 'lodash/first';
import { createSelector } from 'reselect';

import { emptyArray } from 'Lib/constants/placeholder';
import { type GlobalStateType } from 'Lib/reduxType';

import { STATE_LOADING, STATE_SUCCESS } from 'Store/commonActions';
import { DEFAULT_CURRENCY, DEFAULT_PRICE } from 'Store/entities/constants';
import {
  PRODUCT_TYPE_BOOST_SUBSCRIPTION,
  PRODUCT_TYPE_GOLD,
  PRODUCT_TYPE_PLATINUM,
  PRODUCT_TYPE_PLUS,
  PRODUCT_TYPE_SELECT_SUBSCRIPTION,
  PRODUCT_TYPES_ALL_SUBSCRIPTION,
  PRODUCT_TYPES_PREMIUM_SUBSCRIPTION,
  PRODUCT_TYPES_TIERED_SUBSCRIPTION,
  SUPPORTED_PURCHASE_PLATFORMS,
} from 'Store/entities/products.schema';
import {
  getAllSubscriptionProducts,
  getBoostProducts,
  getBoostSubscriptionProducts,
  getBouncerBypassProducts,
  getDiscountedProductsFromProductType,
  getGoldProducts,
  getIntroPriceProducts,
  getNonDiscountedProductsFromProductType,
  getNonDiscountedTieredSubscriptionProducts,
  getOfferEligibleProducts,
  getPlatinumProducts,
  getPlusProducts,
  getPrimetimeBoostProducts,
  getProductListFromType,
  getReadReceiptProducts,
  getReplacementPlusProducts,
  getSubOfferProducts,
  getSuperBoostProducts,
  getSuperLikeProducts,
  getSwipeNoteProducts,
  getTieredSubscriptionProducts,
  getTinderSelectProducts,
  getWeeklyProductsFromProductType,
  selectDiscountedProducts,
} from 'Store/entities/products.selectors';
import type { Product, ProductType } from 'Store/entities/products.types';
import {
  getBaseProductFromList,
  getProductOriginalId,
  getProductsByIds,
} from 'Store/entities/products.utils';
import type { ProductsEntityState } from 'Store/entities/types';
import { getIncentiveIntroPriceProduct, getIncentiveProductType } from 'Store/incentives/selectors';
import { FREE_TRIAL, INTRO_PRICE } from 'Store/profile/constants';
import { getHasGiftCardSubscription } from 'Store/purchase/selectors';

import { selectProfile } from './selectors';
import type {
  ConsumableMerchandising,
  MiscMerchandising,
  Offerings,
  ReadReceipts,
  SubMerchandising,
} from './types';

/**
 * Gets the available product IDs that the user can purchase
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getAvailableProductIds = createSelector(
  selectProfile,
  (profile): Offerings => profile.offerings || emptyArray
);

/**
 * Gets available Tinder Plus products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'plus'`
 */
export const getPlusProductList = createSelector(
  [getAvailableProductIds, getPlusProducts],
  getProductsByIds
);

/**
 * Gets available Tinder Plus products with replacement products for paywalls (weekly plus sub)
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'plus'`
 */
export const getReplacementPlusProductList = createSelector(
  [getAvailableProductIds, getReplacementPlusProducts],
  getProductsByIds
);

/**
 * Gets available Tinder Gold products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'gold'`
 */
export const getGoldProductList = createSelector(
  [getAvailableProductIds, getGoldProducts],
  getProductsByIds
);

/**
 * Gets available Tinder Platinum products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'platinum'`
 */
export const getPlatinumProductList = createSelector(
  [getAvailableProductIds, getPlatinumProducts],
  getProductsByIds
);

/**
 * Gets available tiered subscription products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'plus, gold and platinum'`
 */
export const getTieredSubscriptionProductList = createSelector(
  [getAvailableProductIds, getNonDiscountedTieredSubscriptionProducts],
  getProductsByIds
);

/**
 * Gets available Tinder Boost products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'boost'`
 */
export const getBoostProductList = createSelector(
  [getAvailableProductIds, getBoostProducts],
  getProductsByIds
);

/**
 * Gets available Tinder SuperBoost products list for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'superBoost'`
 */
export const getSuperBoostProductList = createSelector(
  [getAvailableProductIds, getSuperBoostProducts],
  getProductsByIds
);

/**
 *  Gets available Tinder Primetime Boost products list
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'primetimeboost'`
 */
export const getPrimetimeBoostProductList = createSelector(
  [getAvailableProductIds, getPrimetimeBoostProducts],
  getProductsByIds
);

/**
 *  Gets available Tinder SuperBoost product for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entity with `type: 'superBoost'`, if available
 */
export const getSuperBoostProduct = createSelector(
  getSuperBoostProductList,
  (products): Product | undefined => first(products)
);

/**
 * Gets available Tinder BoostSub products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'boostSub'`
 */
export const getBoostSubscriptionProductList = createSelector(
  [getAvailableProductIds, getBoostSubscriptionProducts],
  getProductsByIds
);

/**
 * Gets available Tinder Select products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'selectSubscription'`
 */
export const getTinderSelectProductList = createSelector(
  [getAvailableProductIds, getTinderSelectProducts],
  getProductsByIds
);

/**
 * Gets available Tinder Superlike products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'superLike'`
 */
export const getSuperLikeProductList = createSelector(
  [getAvailableProductIds, getSuperLikeProducts],
  getProductsByIds
);

/**
 * Gets available Read receipt products for paywalls
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `type: 'readreceipt'`
 */
export const getReadReceiptProductList = createSelector(
  [getAvailableProductIds, getReadReceiptProducts],
  getProductsByIds
);

/**
 * Gets available Swipe Note products for paywalls
 *
 * @return product entities with `type: 'swipenote'`
 */
export const getSwipeNoteProductList = createSelector(
  [getAvailableProductIds, getSwipeNoteProducts],
  getProductsByIds
);

/**
 * Gets available Intro price Tinder Gold products for paywalls
 * NOTE: 1. There can only be one intro pricing offer giving to the user at a time
 *       2. An intro pricing offer will not co-exist with a sub offer
 *       3. An intro pricing offer can co-exist with the insendio offers
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `intro-price: true'`
 */
export const getIntroPriceProductList = createSelector(
  [getAvailableProductIds, getIntroPriceProducts],
  getProductsByIds
);

/**
 * Gets available sub offer products for paywalls
 * NOTE: 1. There can only be one sub offer giving to the user at a time
 *       2. A sub offer will not co-exist with an intro pricing offer
 *       3. A sub offer can co-exist with the insendio offers
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with `intro-price: true'`
 */
export const getAvailableSubOfferProduct = createSelector(
  [getAvailableProductIds, getSubOfferProducts],
  (availableIds, subOfferProducts): Product | undefined =>
    first(getProductsByIds(availableIds, subOfferProducts))
);

/**
 * Gets available Insendio offer products
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getOfferEligibleProductList = createSelector(
  [getAvailableProductIds, getOfferEligibleProducts],
  getProductsByIds
);

/**
 * Gets available offer products with discount offer type
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getDiscountedProductList = createSelector(
  [getAvailableProductIds, selectDiscountedProducts],
  getProductsByIds
);

/**
 * Selects the primary intro price product from the list
 *
 * @param  {GlobalStateType} state - top-level state
 * @return primary product entity, if available
 */
export const getPrimaryIntroPriceProduct = createSelector(
  getIntroPriceProductList,
  (products): Product | undefined => first(products)
);

/**
 * Selects the product ID of the gold product associated with the primary intro price product.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getIntroPriceOriginalProductId = createSelector(
  getPrimaryIntroPriceProduct,
  (product): string | undefined => product?.originalProductId
);

/**
 * Selects the product associated with the intro price product.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getIntroPriceOriginalProduct = createSelector(
  [getIntroPriceOriginalProductId, getTieredSubscriptionProductList],
  (id, products): Product | undefined => products.find((product) => product.productId === id)
);

/**
 * Selects the product ID of the incentive intro price product
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getIncentiveIntroPriceOriginalProductId = createSelector(
  getIncentiveIntroPriceProduct,
  (product): string | undefined => product?.productId
);

/**
 * Selects the product associated with the incentive intro price product.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getIncentiveIntroPriceOriginalProduct = createSelector(
  [getIncentiveIntroPriceOriginalProductId, getTieredSubscriptionProductList],
  (id, products): Product | undefined => products.find((product) => product.productId === id)
);

/**
 * Selects the regular product associated with the available sub offer product.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getAvailableSubOfferOriginalProduct = createSelector(
  [getTieredSubscriptionProductList, getAvailableSubOfferProduct],
  (goldProducts, subOfferProduct): Product | undefined =>
    goldProducts.find((product) => product.productId === getProductOriginalId(subOfferProduct))
);

/**
 * Selects available Bouncer Bypass products for paywalls i.e. product entities with type "bouncerbypass".
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getBouncerBypassProductList = createSelector(
  [getAvailableProductIds, getBouncerBypassProducts],
  getProductsByIds
);

/**
 * Selects the available non-discounted products by product type.
 *
 * @param  {GlobalStateType} state - top-level state
 * @param  {ProductType | undefined} productType
 */
export const getProductListByProductType = createSelector(
  [
    getAvailableProductIds,
    (state: GlobalStateType, productType: ProductType | undefined): ProductsEntityState =>
      getNonDiscountedProductsFromProductType(state, productType),
  ],
  getProductsByIds
);

/**
 * Selects the available discounted products by product type.
 *
 * @param  {GlobalStateType} state - top-level state
 * @param  {ProductType} productType
 */
export const getDiscountedProductListByProductType = createSelector(
  [
    getAvailableProductIds,
    (state: GlobalStateType, productType: ProductType): ProductsEntityState =>
      getDiscountedProductsFromProductType(state, productType),
  ],
  getProductsByIds
);

/**
 * Selects the available weekly products by product type.
 *
 * @param  {GlobalStateType} state - top-level state
 * @param  {ProductType | undefined} productType
 */
export const getWeeklyProductListByProductType = createSelector(
  [
    getAvailableProductIds,
    (state: GlobalStateType, productType: ProductType | undefined): ProductsEntityState =>
      getWeeklyProductsFromProductType(state, productType),
  ],
  getProductsByIds
);

/**
 * Selects whether any bouncer bypass products are available to the user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the user has bouncer bypass products
 */
export const getHasBouncerBypassProducts = createSelector(
  getBouncerBypassProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects the product IDs that the user has purchased
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product IDs
 */
export const getPurchaseIds = createSelector(
  selectProfile,
  (profile): string[] => profile.purchase?.purchases || emptyArray
);

/**
 * Selects whether any plus products are available to the user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the user has products
 */
export const getHasPlusProducts = createSelector(
  getPlusProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects whether any gold products are available to the user.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasGoldProducts = createSelector(
  getGoldProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects whether any platinum products are available to the user.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasPlatinumProducts = createSelector(
  getPlatinumProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects whether any Tinder Select products are available to the user.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasSelectProducts = createSelector(
  getTinderSelectProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects whether any superlike products are available to the user.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasSuperLikeProducts = createSelector(
  getSuperLikeProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects whether any boost products are available to the user.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasBoostProducts = createSelector(
  getBoostProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects whether any super boost products are available to the user.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasSuperBoost = createSelector(
  [getSuperBoostProductList, (state) => getHasSubscription(state)], // delay resolving getSuperBoostProductList until getHasSubscription is called
  (products, hasSubscription): boolean => !!(products?.length && hasSubscription)
);

/**
 * Selects whether any intro price products are available to the user.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasIntroPriceProducts = createSelector(
  getIntroPriceProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects whether any read receipt products are available to the user
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the user has read receipt products
 */
export const getHasReadReceiptProducts = createSelector(
  getReadReceiptProductList,
  (products): boolean => !!products?.length
);

/**
 * Selects tiered products that the user has purchased.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getTieredPurchasedSubscriptions = createSelector(
  [getPurchaseIds, getTieredSubscriptionProducts],
  getProductsByIds
);

/**
 * Selects all products that the user has purchased.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getAllPurchasedSubscriptions = createSelector(
  [getPurchaseIds, getAllSubscriptionProducts],
  getProductsByIds
);

/**
 * Selects the currency of purchased product.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getPurchasedProductCurrency = createSelector(
  getTieredPurchasedSubscriptions,
  (products) => products?.[0]?.currency || DEFAULT_CURRENCY
);

/**
 * Selects the productId of the purchased subscription, if available.
 *
 * TODO: typing would be clearer if the fallback was undefined instead of an empty string.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getPurchasedSubscriptionProductId = createSelector(
  getTieredPurchasedSubscriptions,
  (products): string => products?.[0]?.productId || ''
);

/**
 * Selects the id of the user's purchased subscription, if available.
 *
 * TODO: typing would be clearer if the fallback was undefined instead of an empty string.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getPurchasedSubscriptionId = createSelector(
  getTieredPurchasedSubscriptions,
  (products): string => products?.[0]?.id || ''
);

/**
 * Selects the user's subscription type
 *
 * TODO: typing would be clearer if the fallback was undefined instead of an empty string.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return type of the purchased subscription, if available.
 */
export const getPurchasedSubscriptionName = createSelector(
  getTieredPurchasedSubscriptions,
  (products): ProductType | string => products?.[0]?.productType || ''
);

/**
 * Selects the user's subscription terms.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getPurchasedSubscriptionTerms = createSelector(
  getTieredPurchasedSubscriptions,
  (products): number | undefined => products?.[0]?.terms
);

/**
 * Selects the user's subscription term unit.
 *
 * TODO: typing would be clearer if the fallback was undefined instead of an empty string.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getPurchasedSubscriptionTermUnit = createSelector(
  getTieredPurchasedSubscriptions,
  (products): string => products?.[0]?.termsUnit?.toUpperCase() || ''
);

/**
 * Selects whether the user has a subscription product (eg. Tinder Plus OR Gold).
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean => !!products?.length
);

/**
 * Selects whether the user has a subscription product (eg. Tinder Plus OR Gold, or Boost or Select Subs).
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasAnySubscription = createSelector(
  getAllPurchasedSubscriptions,
  (products): boolean => !!products?.length
);

/**
 * Selects whether the user has a Tinder Plus subscription.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasPlusSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (state: GlobalStateType) => getHasPremiumSubscription(state),
  (products, hasPremiumSub): boolean =>
    !hasPremiumSub && products.some((product) => product.productType === PRODUCT_TYPE_PLUS)
);

/**
 * Selects whether the user has a Tinder Gold subscription.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasGoldSubscription = createSelector(
  [getTieredPurchasedSubscriptions, (state: GlobalStateType) => getHasPlatinumSubscription(state)],
  (products, hasPlatinum): boolean =>
    !hasPlatinum && products.some((product) => product.productType === PRODUCT_TYPE_GOLD)
);

/**
 * Selects whether the user has a Tinder Platinum subscription.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasPlatinumSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean => products.some((product) => product.productType === PRODUCT_TYPE_PLATINUM)
);

/**
 * Selects whether the user has a premium subscription.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasPremiumSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean =>
    products.some((product) => PRODUCT_TYPES_PREMIUM_SUBSCRIPTION.includes(product.productType))
);

/**
 * Selects whether the user has a Boost subscription purchased on a different platform.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasNativeBoostSubscription = createSelector(
  getAllPurchasedSubscriptions,
  (products): boolean =>
    products.some(
      (product) =>
        product.productType === PRODUCT_TYPE_BOOST_SUBSCRIPTION &&
        !SUPPORTED_PURCHASE_PLATFORMS.has(product.platform)
    )
);

/**
 * Selects whether the user has a Boost subscription.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasBoostSubscription = createSelector(
  getAllPurchasedSubscriptions,
  (products): boolean =>
    products.some(
      (product) =>
        product.productType === PRODUCT_TYPE_BOOST_SUBSCRIPTION &&
        SUPPORTED_PURCHASE_PLATFORMS.has(product.platform)
    )
);

/**
 * Selects whether the user has a Tinder Select subscription.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getHasSelectSubscription = createSelector(
  getAllPurchasedSubscriptions,
  (products): boolean =>
    products.some((product) => product.productType === PRODUCT_TYPE_SELECT_SUBSCRIPTION)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns whether the user has a tinder select subscription from a different platform.
 */
export const getHasNativeSelectSubscription = createSelector(
  getAllPurchasedSubscriptions,
  (products): boolean =>
    products.some(
      (product) =>
        product.productType === PRODUCT_TYPE_SELECT_SUBSCRIPTION &&
        !SUPPORTED_PURCHASE_PLATFORMS.has(product.platform)
    )
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns product details of the purchased boost subscription.
 */
export const getPurchasedBoostSubscriptionProduct = createSelector(
  getAllPurchasedSubscriptions,
  (products): Product | undefined =>
    products.find((product) => product.productType === PRODUCT_TYPE_BOOST_SUBSCRIPTION)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns product details of the purchased Tinder Select subscription.
 */
export const getPurchasedSelectSubscriptionProduct = createSelector(
  getAllPurchasedSubscriptions,
  (products): Product | undefined =>
    products.find((product) => product.productType === PRODUCT_TYPE_SELECT_SUBSCRIPTION)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns whether the user has an active intro price subscription.
 */
export const getHasIntroPriceSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean => products.some((product) => product.originalProductId)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns original product id of purchased intro price product.
 */
export const getPurchasedIntroPriceGoldProductId = createSelector(
  getTieredPurchasedSubscriptions,
  (products): string => {
    const firstProduct = first(products);
    return firstProduct?.originalProductId || '';
  }
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns the gold product associated with the purchased intro price product.
 */
export const getPurchasedIntroPriceGoldProduct = createSelector(
  [getPurchasedIntroPriceGoldProductId, getGoldProductList],
  (id, products): Product | undefined =>
    first(products?.filter((product) => product.productId === id))
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns whether the user has a plus, gold or platinum web compatible subscription.
 */
export const getHasWebSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean =>
    !!products?.filter(
      (product) =>
        PRODUCT_TYPES_TIERED_SUBSCRIPTION.includes(product.productType) &&
        SUPPORTED_PURCHASE_PLATFORMS.has(product.platform)
    ).length
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return  whether the user has a plus, gold, platinum or Boost web compatible subscription
 */
export const getHasAnyWebSubscription = createSelector(
  getAllPurchasedSubscriptions,
  (products): boolean =>
    !!products?.filter(
      (product) =>
        PRODUCT_TYPES_ALL_SUBSCRIPTION.includes(product.productType) &&
        SUPPORTED_PURCHASE_PLATFORMS.has(product.platform)
    ).length
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns whether the user has native sub
 */
export const getHasNativeSubscription = createSelector(
  [getHasSubscription, getHasWebSubscription, getHasGiftCardSubscription],
  (hasSubscription, hasWebSubscription, hasGiftCardSubscription): boolean =>
    hasSubscription && !hasWebSubscription && !hasGiftCardSubscription
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns whether the user's subscription is a free trial incentive.
 *   Need both checks for backward compatibility. Only incentivesType check is needed after api change
 */
export const getHasFreeTrialIncentiveSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean =>
    !!products?.filter((product) => product.isIncentives || product.incentivesType === FREE_TRIAL)
      .length
);

/**
 * Determines whether the user's current subscription is from an incentive of type intro_price
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the user's subscription is a intro price incentive
 */
export const getHasIntroPriceIncentiveSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean =>
    !!products?.filter((product) => product.incentivesType === INTRO_PRICE).length
);

/**
 * Determines whether the user's current subscription is from an incentive
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the user's subscription is an incentive
 */
export const getHasIncentiveSubscription = createSelector(
  getTieredPurchasedSubscriptions,
  (products): boolean =>
    !!products?.filter((product) => product.isIncentives || product.incentivesType).length
);

/**
 * Returns the incentive renewal product details
 *
 * @param  {GlobalStateType} state - top-level state
 * @return renewal product for incentives
 */
export const getIncentiveRenewalProduct = createSelector(
  getPlusProductList,
  getGoldProductList,
  getPlatinumProductList,
  getIncentiveProductType,
  getPurchasedSubscriptionName,
  (
    plusProducts,
    goldProducts,
    platinumProducts,
    incentiveProductType,
    subscriptionProductType
  ): Product | undefined =>
    getBaseProductFromList(
      getProductListFromType(
        incentiveProductType || subscriptionProductType,
        plusProducts,
        goldProducts,
        platinumProducts
      )
    )
);

/**
 * Returns the price of the user's subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return price of the purchased subscription
 */
export const getPurchasedSubscriptionPrice = createSelector(
  getTieredPurchasedSubscriptions,
  (products: Product[]): number => products?.[0]?.price ?? DEFAULT_PRICE
);

/**
 * Gets the read receipt details in profile
 *
 * @param  {GlobalStateType} state - top-level state
 * @return object consisting of read receipt details
 */
export const getReadReceipts = createSelector(
  selectProfile,
  (profile): ReadReceipts => profile.readReceipts
);

/**
 * Get remaining read receipts
 *
 * @return Number of remaining read receipts
 */
export const getRemainingReadReceipts = createSelector(
  getReadReceipts,
  (readReceipts): number => readReceipts?.remaining ?? 0
);

/**
 * Get error on consuming read receipts
 *
 * @param  {GlobalStateType} state - top-level state
 * @return Read receipt error
 */
export const getReadReceiptError = createSelector(
  selectProfile,
  (profile) => profile.readReceiptsState?.error
);

/**
 * Gets status of read receipt subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return If read receipts are enabled or not
 */
export const getReadReceiptSubscriptionEnabled = createSelector(
  selectProfile,
  (profile): boolean | undefined => profile.readReceiptsState?.enabled
);

/**
 * Get loading state on consuming read receipts
 *
 * @param  {GlobalStateType} state - top-level state
 * @return Read receipt loading state
 */
export const getIsReadReceiptLoading = createSelector(
  selectProfile,
  (profile) => profile.readReceiptsState?.status === STATE_LOADING
);

/**
 * Get success state on consuming read receipts
 *
 * @param  {GlobalStateType} state - top-level state
 * @return Read receipt consumption success state
 */
export const getIsReadReceiptConsumptionSuccess = createSelector(
  selectProfile,
  (profile) => profile.readReceiptsState?.status === STATE_SUCCESS
);

/**
 * Get user match id for read receipt
 *
 * @param  {GlobalStateType} state - top-level state
 * @return Read receipt match id
 */
export const getReadReceiptMatchId = createSelector(
  selectProfile,
  (profile) => profile.readReceiptsState?.matchId
);

/**
 * Returns the number of bouncer bypasses a user has remaining
 *
 * @param  {GlobalStateType} state - top-level state
 * @return number of bouncer bypasses remaining
 */
export const getBouncerBypassesRemaining = createSelector(
  selectProfile,
  (profile): number => profile.bouncerBypass?.total_bouncer_bypass_remaining ?? 0
);

/**
 * Returns the number of super likes a user has remaining
 *
 * @param  {GlobalStateType} state - top-level state
 * @return number of super likes remaining
 */
export const getSuperLikesRemaining = createSelector(
  selectProfile,
  (profile): number => profile.superlikes?.remaining ?? 0
);

/**
 * Returns date at which user gets more superlikes
 *
 * @param  {GlobalStateType} state - top-level state
 * @return date when super likes resets
 */
export const getSuperLikesResetTime = createSelector(
  selectProfile,
  (profile): Date | undefined => profile.superlikes?.resets_at
);

/**
 * Returns the number of likes a user has remaining
 *
 * @param  {GlobalStateType} state - top-level state
 * @return  number of likes remaining
 */
export const getLikesRemaining = createSelector(
  selectProfile,
  (profile): number => profile.likes?.likes_remaining ?? 0
);

/**
 * Returns the time remaining until user gets more likes
 *
 * @param  {GlobalStateType} state - top-level state
 * @return amount of time until likes renews
 */
export const getLikesLimitedUntil = createSelector(
  selectProfile,
  (profile): number | undefined => profile.likes?.rate_limited_until
);

/**
 * Gets Merchandising products for subs
 *
 * @param  {GlobalStateType} state - top-level state
 * @return An Object consisting of merchandising products under each sub tier
 */
export const getSubscriptionMerchandising = createSelector(
  selectProfile,
  (profile): SubMerchandising => profile.subMerchandisingProductData
);

/**
 * Gets Merchandising products for consumables
 *
 * @param  {GlobalStateType} state - top-level state
 * @return An Object consisting of merchandising products under each sub tier
 */
export const getConsumableMerchandising = createSelector(
  selectProfile,
  (profile): ConsumableMerchandising => profile.consumableMerchandising
);

/**
 * Gets paywall, platinum paywall and merch page feature order for all subscription types
 *
 * @param  {GlobalStateType} state - top-level state
 * @return An Object consisting of feature order for each sub tier
 */
export const getOfferingFeatures = createSelector(
  selectProfile,
  (profile): SubMerchandising => profile.featureOrdering
);

/**
 * Gets Merchandising page data for subs
 *
 * @param  {GlobalStateType} state - top-level state
 * @return An Object consisting of merchandising page data under each sub tier
 */
export const getSubscriptionMerchandisingPageData = createSelector(
  selectProfile,
  (profile): SubMerchandising => profile.subMerchandisingPageData
);

/**
 * Gets Misc Merchandising data for revenue features
 *
 * @param  {GlobalStateType} state - top-level state
 * @return An Object consisting of merchandising data for revenue features
 */
export const getMiscMerchandisingData = createSelector(
  selectProfile,
  (profile): MiscMerchandising => profile.miscMerchandising
);

/**
 * Gets Subscription Card Ordering data for revenue features
 *
 * @param  {GlobalStateType} state - top-level state
 * @return An Array consisting of Subscription Card Ordering data for revenue features
 */
export const getSubscriptionCardOrdering = createSelector(
  selectProfile,
  (profile): string[] => profile.miscMerchandising?.subscriptionCardOrdering ?? emptyArray
);

/**
 * Gets Controlla Carousel Ordering data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return An Array consisting of Controlla Carousel Ordering data
 */
export const getControllaCarouselOrdering = createSelector(
  selectProfile,
  (profile): string[] => profile.miscMerchandising?.controllaCarouselOrdering ?? emptyArray
);

/**
 * Gets landing card for sub merch page
 *
 * @param  {GlobalStateType} state - top-level state
 * @return landing card for sub merch page
 */
export const getSubPageLandingCard = createSelector(
  selectProfile,
  (profile): string | null | undefined => profile.miscMerchandising?.landingCard
);

/**
 * Gets sub renewal banner text for sub merch page
 *
 * @param  {GlobalStateType} state - top-level state
 * @return sub renewal banner heading and description
 */
export const getSubPageRenewalBannerText = createSelector(
  selectProfile,
  (profile) => profile.miscMerchandising?.subExpirationBanner
);
